import React, { useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";
import NotFound from "../ul/NotFound";

const WithdrawAnalyticsChart = ({ withdrawData = [] }) => {
  const [filterStatus, setFilterStatus] = useState("");

  const filteredData = useMemo(() => {
    if (!filterStatus) return withdrawData;
    return withdrawData.filter((item) =>
      filterStatus === "Success" ? item.status === true : item.status === false
    );
  }, [filterStatus, withdrawData]);

  const aggregated = useMemo(() => {
    const map = {};

    for (const item of filteredData) {
      const d = new Date(item.createdAt);
      const date = `${String(d.getDate()).padStart(2, "0")}/${String(
        d.getMonth() + 1
      ).padStart(2, "0")}`;

      if (!map[date]) {
        map[date] = {
          Success: { amount: 0, count: 0 },
          Pending: { amount: 0, count: 0 },
        };
      }

      if (item.status) {
        map[date].Success.amount += Number(item.u_Amount || 0);
        map[date].Success.count += 1;
      } else {
        map[date].Pending.amount += Number(item.u_Amount || 0);
        map[date].Pending.count += 1;
      }
    }

    const dates = Object.keys(map).sort((a, b) => {
      const [dayA, monthA] = a.split("/").map(Number);
      const [dayB, monthB] = b.split("/").map(Number);
      return monthA - monthB || dayA - dayB;
    });

    return {
      dates,
      successAmount: dates.map((d) => map[d].Success.amount),
      pendingAmount: dates.map((d) => map[d].Pending.amount),
      successCount: dates.map((d) => map[d].Success.count),
      pendingCount: dates.map((d) => map[d].Pending.count),
    };
  }, [filteredData]);

  const hasData = withdrawData.length > 0;

  const chartOption = {
    tooltip: {
      trigger: "axis",
      backgroundColor: "rgba(255, 255, 255, 0.95)",
      borderColor: "#e2e8f0",
      borderWidth: 1,
      padding: 16,
      textStyle: {
        color: "#0f172a",
        fontSize: 13,
      },
      formatter: function (params) {
        if (!params || params.length === 0) return "";

        const dateValue = params[0]?.axisValue || "N/A";
        const dataIndex = params[0]?.dataIndex;

        if (dataIndex === undefined) return "";

        const successAmount = aggregated.successAmount[dataIndex] || 0;
        const pendingAmount = aggregated.pendingAmount[dataIndex] || 0;
        const successCount = aggregated.successCount[dataIndex] || 0;
        const pendingCount = aggregated.pendingCount[dataIndex] || 0;
        const totalAmount = successAmount + pendingAmount;
        const totalCount = successCount + pendingCount;

        return `
          <div style="min-width: 200px;">
            <div style="font-weight: 700; font-size: 14px; color: #0f172a; margin-bottom: 12px; padding-bottom: 8px; border-bottom: 2px solid #e2e8f0;">
              📅 ${dateValue}
            </div>
            <div style="margin-bottom: 8px;">
              <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px;">
                <span style="display: flex; align-items: center; gap: 6px;">
                  <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: linear-gradient(180deg, #facc15, #ca8a04);"></span>
                  <strong>Success</strong>
                </span>
                <span style="font-weight: 600;">₹${successAmount.toLocaleString()}</span>
              </div>
              <div style="font-size: 12px; color: #64748b; margin-left: 16px;">
                ${successCount} transaction${successCount !== 1 ? "s" : ""}
              </div>
            </div>
            <div style="margin-bottom: 8px;">
              <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px;">
                <span style="display: flex; align-items: center; gap: 6px;">
                  <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: linear-gradient(180deg, #ef4444, #b91c1c);"></span>
                  <strong>Pending</strong>
                </span>
                <span style="font-weight: 600;">₹${pendingAmount.toLocaleString()}</span>
              </div>
              <div style="font-size: 12px; color: #64748b; margin-left: 16px;">
                ${pendingCount} transaction${pendingCount !== 1 ? "s" : ""}
              </div>
            </div>
            <div style="margin-top: 12px; padding-top: 8px; border-top: 1px solid #e2e8f0;">
              <div style="display: flex; justify-content: space-between; font-weight: 700; color: #0f172a;">
                <span>Total</span>
                <span>₹${totalAmount.toLocaleString()}</span>
              </div>
              <div style="font-size: 12px; color: #64748b; text-align: right;">
                ${totalCount} transaction${totalCount !== 1 ? "s" : ""}
              </div>
            </div>
          </div>
        `;
      },
    },
    legend: {
      data: ["Success", "Pending"],
      textStyle: {
        color: "#0f172a",
        fontWeight: 600,
        fontSize: 13,
      },
      itemWidth: 28,
      itemHeight: 14,
      top: 10,
    },
    grid: {
      left: "3%",
      right: "4%",
      bottom: "3%",
      top: "15%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      data: aggregated.dates,
      axisLabel: {
        color: "#334155",
        fontWeight: 600,
        fontSize: 12,
      },
      axisLine: {
        lineStyle: { color: "#cbd5e1" },
      },
    },
    yAxis: {
      type: "value",
      name: "Amount (₹)",
      nameTextStyle: {
        color: "#64748b",
        fontWeight: 600,
        fontSize: 12,
      },
      axisLabel: {
        color: "#334155",
        fontWeight: 600,
        fontSize: 11,
        formatter: (value) =>
          `₹${value >= 1000 ? (value / 1000).toFixed(0) + "k" : value}`,
      },
      splitLine: {
        lineStyle: {
          type: "dashed",
          color: "#e2e8f0",
          opacity: 0.6,
        },
      },
    },
    series: [
      {
        name: "Success",
        type: "bar",
        data: aggregated.successAmount,
        barWidth: "35%",
        itemStyle: {
          borderRadius: [8, 8, 0, 0],
          color: {
            type: "linear",
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: "#facc15" },
              { offset: 1, color: "#ca8a04" },
            ],
          },
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: "rgba(250, 204, 21, 0.5)",
          },
        },
      },
      {
        name: "Pending",
        type: "bar",
        data: aggregated.pendingAmount,
        barWidth: "35%",
        itemStyle: {
          borderRadius: [8, 8, 0, 0],
          color: {
            type: "linear",
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: "#ef4444" },
              { offset: 1, color: "#b91c1c" },
            ],
          },
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: "rgba(239, 68, 68, 0.5)",
          },
        },
      },
    ],
  };

  const statusOptions = ["All", "Success", "Pending"];
  const totalSuccess = aggregated.successAmount.reduce((a, b) => a + b, 0);
  const totalPending = aggregated.pendingAmount.reduce((a, b) => a + b, 0);
  const totalTransactions =
    aggregated.successCount.reduce((a, b) => a + b, 0) +
    aggregated.pendingCount.reduce((a, b) => a + b, 0);

  return (
    <div className="p-6 md:p-10 rounded-3xl bg-gradient-to-br from-white to-slate-50 shadow-lg">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-8">
        <div className="flex items-center gap-3">
          <div className="w-1 h-7 bg-gradient-to-b from-purple-500 to-amber-500 rounded-full" />
          <h3 className="text-lg md:text-xl font-bold text-slate-800">
            Withdraw Analytics
          </h3>
        </div>

        <div className="flex flex-wrap gap-4 items-center">
          <select
            value={filterStatus || "All"}
            onChange={(e) =>
              setFilterStatus(e.target.value === "All" ? "" : e.target.value)
            }
            className="px-5 py-3 rounded-xl border-2 border-slate-300 font-bold bg-white shadow-md hover:border-slate-400 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-slate-400"
          >
            {statusOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!hasData ? (
        <div className="flex justify-center items-center h-96 bg-slate-50 rounded-2xl">
          <NotFound title="No Withdraw Data Available" />
        </div>
      ) : filteredData.length === 0 ? (
        <div className="flex justify-center items-center h-96 bg-slate-50 rounded-2xl ">
          <NotFound title={`No ${filterStatus} Transactions Found`} />
        </div>
      ) : (
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <ReactECharts option={chartOption} style={{ height: "500px" }} />
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Total Transactions Card */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-300"></div>
              <div className="relative bg-gradient-to-br from-blue-500 to-blue-700 p-6 md:p-8 rounded-2xl shadow-2xl transform group-hover:-translate-y-2 transition-all duration-300">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center text-2xl md:text-3xl">
                    📊
                  </div>
                  <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse shadow-lg shadow-green-400/50"></div>
                </div>
                <p className="text-blue-100 text-xs md:text-sm font-bold uppercase tracking-wider mb-2">
                  Total Transactions
                </p>
                <p className="text-white text-xl md:text-2xl font-bold tracking-tight">
                  {totalTransactions.toLocaleString()}
                </p>
                <div className="mt-4 h-1 bg-white/20 rounded-full overflow-hidden">
                  <div className="h-full bg-white/60 rounded-full w-full animate-pulse"></div>
                </div>
              </div>
            </div>

            {/* Total Amount Card */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-300"></div>
              <div className="relative bg-gradient-to-br from-emerald-500 to-emerald-700 p-6 md:p-8 rounded-2xl shadow-2xl transform group-hover:-translate-y-2 transition-all duration-300">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center text-2xl md:text-3xl">
                    💰
                  </div>
                  <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse shadow-lg shadow-yellow-400/50"></div>
                </div>
                <p className="text-emerald-100 text-xs md:text-sm font-bold uppercase tracking-wider mb-2">
                  Total Amount
                </p>
                <p className="text-white text-xl md:text-2xl font-bold tracking-tight">
                  ₹{(totalSuccess + totalPending).toLocaleString()}
                </p>
                <div className="mt-4 h-1 bg-white/20 rounded-full overflow-hidden">
                  <div className="h-full bg-white/60 rounded-full w-full animate-pulse"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WithdrawAnalyticsChart;
